import { z } from 'zod'

import type { StatusTone } from '@ems/ui/patterns/status-badge'

import type { LineInput } from '#admin/features/billing/api.ts'
import { api } from '#admin/lib/api-client.ts'

export const CREDIT_NOTE_STATUSES = ['draft', 'issued', 'voided'] as const
export type CreditNoteStatus = (typeof CREDIT_NOTE_STATUSES)[number]

/** Credit notes: the correction once money has moved. They reduce what is owed, never what was paid. */
const creditNoteSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.enum(CREDIT_NOTE_STATUSES),
  invoiceId: z.string(),
  invoiceNumber: z.string().nullable(),
  /** The customer on the invoice. `.default`: the console can be deployed ahead of the API. */
  billToName: z.string().nullable().default(null),
  currency: z.string(),
  subtotal: z.number(),
  taxTotal: z.number(),
  total: z.number(),
  reason: z.string(),
  issuedAt: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
})

export type CreditNote = z.infer<typeof creditNoteSchema>

const base = '/v1/billing/admin/credit-notes'

export function fetchCreditNotes(invoiceId?: string, signal?: AbortSignal) {
  return api.get(invoiceId === undefined ? base : `${base}?invoiceId=${invoiceId}`, {
    schema: z.object({ items: z.array(creditNoteSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function draftCreditNote(body: { invoiceId: string; reason: string; lines: LineInput[] }) {
  return api.post(base, { schema: creditNoteSchema, body })
}

export function issueCreditNote(id: string) {
  return api.post(`${base}/${id}/issue`, { schema: creditNoteSchema })
}

export function voidCreditNote(id: string, reason: string) {
  return api.post(`${base}/${id}/void`, { schema: creditNoteSchema, body: { reason } })
}

const CREDIT_NOTE_TONE: Record<CreditNote['status'], StatusTone> = {
  draft: 'neutral',
  issued: 'success',
  voided: 'danger',
}

export function creditNoteTone(status: CreditNote['status']): StatusTone {
  return CREDIT_NOTE_TONE[status]
}
